/** Client-side checks only; server must enforce full validation. */

import { NETWORK_ERROR } from '@model/errors';

export const MAX_HTML_UPLOAD_BYTES = 100 * 1024 * 1024;

export const MAX_HTML_UPLOAD_MB = MAX_HTML_UPLOAD_BYTES / (1024 * 1024);

export const HTML_UPLOAD_ACCEPT = '.html,.htm,text/html,application/xhtml+xml';

const HTML_BLOB_TYPES = new Set([
  'text/html',
  'application/xhtml+xml',
  'application/octet-stream',
  '',
]);

/**
 * @param {File|Blob|null} file
 * @returns {string|null} Error message, or null if no client-side error.
 */
export function getHtmlUploadClientError(file) {
  if (!file || !(file instanceof File)) {
    return 'Please choose a file to upload.';
  }
  if (file.size > MAX_HTML_UPLOAD_BYTES) {
    return `File size must not exceed ${MAX_HTML_UPLOAD_MB} MB.`;
  }
  const lowerName = String(file.name || '').toLowerCase();
  if (!lowerName.endsWith('.html') && !lowerName.endsWith('.htm')) {
    return 'Only HTML files are supported.';
  }
  return null;
}

const DEFAULT_UPLOAD_ERROR = 'HTML upload failed. Please try again.';
const DEFAULT_DOWNLOAD_ERROR = 'HTML download failed. Please try again.';

const JSON_BLOB_MAX_BYTES = 8192;

const HTML_ASSET_BASE_HEADERS = [
  'x-html-asset-base-url',
  'x-html-base-url',
  'x-asset-base-url',
];

/**
 * @param {object|null|undefined} headers
 * @returns {string|null}
 */
export function getHtmlAssetBaseUrlFromHeaders(headers = {}) {
  if (!headers || typeof headers !== 'object') {
    return null;
  }
  const normalizedHeaders = Object.fromEntries(
    Object.entries(headers).map(([key, value]) => [key.toLowerCase(), value]),
  );
  for (const headerName of HTML_ASSET_BASE_HEADERS) {
    const value = normalizedHeaders[headerName];
    if (typeof value === 'string' && value.trim()) {
      const trimmed = value.trim();
      return trimmed.endsWith('/') ? trimmed : `${trimmed}/`;
    }
  }
  return null;
}

/**
 * Inject a base href so relative assets resolve when HTML is rendered via srcdoc/blob.
 * @param {string} html
 * @param {string} baseUrl
 * @returns {string}
 */
export function injectBaseHrefIntoHtml(html, baseUrl) {
  if (!baseUrl || typeof html !== 'string') {
    return html;
  }
  const safeBaseUrl = baseUrl.replace(/"/g, '&quot;');
  const baseTag = `<base href="${safeBaseUrl}">`;
  if (/<head[\s>]/i.test(html)) {
    return html.replace(/<head(\s[^>]*)?>/i, (match) => `${match}${baseTag}`);
  }
  if (/<html[\s>]/i.test(html)) {
    return html.replace(
      /<html(\s[^>]*)?>/i,
      (match) => `${match}<head>${baseTag}</head>`,
    );
  }
  return `<head>${baseTag}</head>${html}`;
}

function resolveHtmlAssetBaseUrl(headers = {}) {
  return getHtmlAssetBaseUrlFromHeaders(headers);
}

/**
 * @param {string} url
 * @returns {boolean}
 */
export function isRelativeAssetUrl(url) {
  const value = String(url || '').trim();
  if (!value || value.startsWith('#')) {
    return false;
  }
  if (/^(data:|blob:|mailto:|javascript:)/i.test(value)) {
    return false;
  }
  if (/^https?:\/\//i.test(value)) {
    return false;
  }
  if (/^\/\//.test(value)) {
    return false;
  }
  return true;
}

/**
 * Resolve a relative asset path against the stored HTML file key (blob path).
 * @param {string} htmlFileKey
 * @param {string} relativePath
 * @returns {string|null}
 */
export function resolveHtmlAssetFileKey(htmlFileKey, relativePath) {
  const normalizedPath = String(relativePath || '').trim();
  if (!normalizedPath || !isRelativeAssetUrl(normalizedPath)) {
    return null;
  }
  const baseKey = normalizeHtmlFileKey(htmlFileKey);
  const baseSegments = baseKey.includes('/')
    ? baseKey.slice(0, baseKey.lastIndexOf('/')).split('/').filter(Boolean)
    : [];
  const relSegments = normalizedPath.split('/').filter(Boolean);
  const resolved = [...baseSegments];
  for (const segment of relSegments) {
    if (segment === '..') {
      resolved.pop();
    } else if (segment !== '.') {
      resolved.push(segment);
    }
  }
  return resolved.join('/') || null;
}

function injectCharsetMetaIfMissing(html) {
  if (/charset=/i.test(html)) {
    return html;
  }
  const meta = '<meta charset="utf-8">';
  if (/<head[\s>]/i.test(html)) {
    return html.replace(/<head(\s[^>]*)?>/i, (match) => `${match}${meta}`);
  }
  return `${meta}${html}`;
}

/**
 * Read the CSP nonce from the host app shell (see public/index.html).
 * @returns {string}
 */
export function getPageCspNonce() {
  if (typeof document === 'undefined') {
    return '';
  }
  const nonceEl = document.querySelector('style[nonce], script[nonce]');
  if (!nonceEl) {
    return '';
  }
  return nonceEl.nonce || nonceEl.getAttribute('nonce') || '';
}

/**
 * Gateway CSP requires a matching nonce on inline <style> and <script> in srcdoc iframes.
 * @param {string} html
 * @param {string} nonce
 * @returns {string}
 */
export function injectCspNonceIntoHtml(html, nonce) {
  if (!html || !nonce) {
    return html;
  }
  const safeNonce = String(nonce).replace(/"/g, '');
  const withStyleNonce = html.replace(
    /<style((?:\s[^>]*)?)>/gi,
    (match, attrs = '') => {
      if (/\bnonce\s*=/i.test(attrs)) {
        return match;
      }
      return `<style nonce="${safeNonce}"${attrs}>`;
    },
  );
  return withStyleNonce.replace(
    /<script((?:\s[^>]*)?)>/gi,
    (match, attrs = '') => {
      if (/\bnonce\s*=/i.test(attrs)) {
        return match;
      }
      return `<script nonce="${safeNonce}"${attrs}>`;
    },
  );
}

const MAX_INLINED_HTML_ASSETS = 25;
const MAX_INLINED_ASSET_BYTES = 5 * 1024 * 1024;
const STYLESHEET_LINK_RE = /<link\b[^>]*\brel=["']stylesheet["'][^>]*>/gi;
const CSS_URL_RE = /url\(\s*(['"]?)([^'")]+)\1\s*\)/gi;

async function readDownloadedAssetText(res) {
  const blob = extractHtmlDownloadBlob(res?.data ?? res);
  if (!blob || blob.type === 'application/json') {
    return null;
  }
  const text = await blob.text();
  if (text.trimStart().startsWith('{')) {
    try {
      JSON.parse(text);
      return null;
    } catch {
      return text;
    }
  }
  return text;
}

async function readDownloadedAssetBlob(res) {
  const blob = extractHtmlDownloadBlob(res?.data ?? res);
  if (!blob || blob.type === 'application/json') {
    return null;
  }
  if (blob.size > MAX_INLINED_ASSET_BYTES) {
    return null;
  }
  return blob;
}

function blobToDataUrl(blob) {
  return new Promise((resolve, reject) => {
    const reader = new FileReader();
    reader.onload = () => resolve(reader.result);
    reader.onerror = reject;
    reader.readAsDataURL(blob);
  });
}

async function rewriteRelativeUrlsInCss(
  cssText,
  cssFileKey,
  downloadHtml,
  cache,
) {
  let result = cssText;
  const replacements = [];
  let match = CSS_URL_RE.exec(cssText);
  while (match) {
    const rawUrl = match[2].trim();
    if (isRelativeAssetUrl(rawUrl)) {
      replacements.push({ token: match[0], rawUrl });
    }
    match = CSS_URL_RE.exec(cssText);
  }
  CSS_URL_RE.lastIndex = 0;

  for (const { token, rawUrl } of replacements) {
    const assetKey = resolveHtmlAssetFileKey(cssFileKey, rawUrl);
    if (!assetKey) {
      continue;
    }
    let dataUrl = cache.get(assetKey);
    if (!dataUrl) {
      const blob = await readDownloadedAssetBlob(await downloadHtml(assetKey));
      if (!blob) {
        continue;
      }
      dataUrl = await blobToDataUrl(blob);
      cache.set(assetKey, dataUrl);
    }
    result = result.split(token).join(`url(${dataUrl})`);
  }
  return result;
}

async function inlineRelativeStylesheets(html, fileKey, downloadHtml) {
  const cache = new Map();
  let assetCount = 0;
  const links = [];
  let linkMatch = STYLESHEET_LINK_RE.exec(html);
  while (linkMatch) {
    const tag = linkMatch[0];
    const hrefMatch = /\bhref=["']([^"']+)["']/i.exec(tag);
    if (hrefMatch && isRelativeAssetUrl(hrefMatch[1])) {
      links.push({ tag, href: hrefMatch[1] });
    }
    linkMatch = STYLESHEET_LINK_RE.exec(html);
  }
  STYLESHEET_LINK_RE.lastIndex = 0;

  let result = html;
  for (const { tag, href } of links) {
    if (assetCount >= MAX_INLINED_HTML_ASSETS) {
      break;
    }
    const assetKey = resolveHtmlAssetFileKey(fileKey, href);
    if (!assetKey) {
      continue;
    }
    let cssText = await readDownloadedAssetText(await downloadHtml(assetKey));
    if (!cssText) {
      continue;
    }
    cssText = await rewriteRelativeUrlsInCss(
      cssText,
      assetKey,
      downloadHtml,
      cache,
    );
    assetCount += 1;
    const safeHref = href.replace(/"/g, '&quot;');
    result = result.replace(
      tag,
      `<style data-inlined-from="${safeHref}">${cssText}</style>`,
    );
  }
  return result;
}

/**
 * Prepare streamed HTML for iframe display: charset meta, optional base href,
 * and inline relative stylesheets fetched via the same download API.
 * @param {string} htmlText
 * @param {string} fileKey
 * @param {{ assetBaseUrl?: string|null, downloadHtml?: Function|null }} options
 * @returns {Promise<string>}
 */
export async function prepareHtmlForIframeDisplay(
  htmlText,
  fileKey,
  { assetBaseUrl = null, downloadHtml = null } = {},
) {
  let html = injectCharsetMetaIfMissing(htmlText);
  if (assetBaseUrl) {
    html = injectBaseHrefIntoHtml(html, assetBaseUrl);
  }
  if (downloadHtml && fileKey) {
    html = await inlineRelativeStylesheets(html, fileKey, downloadHtml);
  }
  const nonce = getPageCspNonce();
  if (nonce) {
    html = injectCspNonceIntoHtml(html, nonce);
  }
  return html;
}

/**
 * Formats HTML upload API error bodies: { message, errors[] } or { success: false, message }.
 * Used only by the HTML upload inline UI — not the global REST error reporter.
 * @param {object|null|undefined} payload
 * @returns {string|null}
 */
export function formatHtmlUploadApiErrorPayload(payload) {
  if (!payload || typeof payload !== 'object') {
    return null;
  }
  const message =
    typeof payload.message === 'string' ? payload.message.trim() : '';
  const errorDetails = Array.isArray(payload.errors)
    ? payload.errors
        .filter((item) => typeof item === 'string' && item.trim())
        .map((item) => item.trim())
    : [];

  if (message && errorDetails.length) {
    const extra = errorDetails.filter((detail) => !message.includes(detail));
    if (extra.length) {
      return `${message}. ${extra.join('. ')}`;
    }
    return message;
  }
  if (message) {
    return message;
  }
  if (errorDetails.length) {
    return errorDetails.join('. ');
  }
  return null;
}

function getHtmlUploadInlineErrorMessage(resOrError) {
  if (!resOrError) {
    return DEFAULT_UPLOAD_ERROR;
  }
  if (typeof resOrError === 'string') {
    return resOrError;
  }

  const fromPayload = formatHtmlUploadApiErrorPayload(resOrError);
  if (fromPayload) {
    return fromPayload;
  }

  const genericInterceptorMessage = NETWORK_ERROR.INTERNAL_SERVER_ERROR;
  if (
    typeof resOrError.message === 'string' &&
    resOrError.message.length &&
    resOrError.message !== genericInterceptorMessage
  ) {
    return resOrError.message;
  }

  return DEFAULT_UPLOAD_ERROR;
}

function getHtmlDownloadInlineErrorMessage(resOrError) {
  if (!resOrError) {
    return DEFAULT_DOWNLOAD_ERROR;
  }
  if (typeof resOrError === 'string') {
    return resOrError;
  }
  if (
    resOrError.success === false &&
    typeof resOrError.message === 'string' &&
    resOrError.message.length
  ) {
    return resOrError.message;
  }
  if (typeof resOrError.message === 'string' && resOrError.message.length) {
    return resOrError.message;
  }
  return DEFAULT_DOWNLOAD_ERROR;
}

/**
 * Message for the HTML upload modal (inline UI only).
 * @param {object|Error|string|null|undefined} resOrError
 * @returns {string}
 */
export function getHtmlUploadApiErrorMessage(resOrError) {
  return getHtmlUploadInlineErrorMessage(resOrError);
}

/**
 * Message for failed HTML download in the HTML viewer (inline UI only).
 * @param {object|Error|string|null|undefined} resOrError
 * @returns {string}
 */
export function getHtmlDownloadApiErrorMessage(resOrError) {
  return getHtmlDownloadInlineErrorMessage(resOrError);
}

/**
 * Message for failed HTML download when the error response may be a Blob.
 * @param {object|Error|string|null|undefined} resOrError
 * @returns {Promise<string>}
 */
export async function getHtmlDownloadApiErrorMessageAsync(resOrError) {
  const blobData = resOrError?.response?.data;
  if (blobData instanceof Blob) {
    const fromBlob = await parseHtmlDownloadBlobError(blobData);
    if (fromBlob) {
      return fromBlob;
    }
  }
  return getHtmlDownloadInlineErrorMessage(resOrError);
}

/**
 * @param {object|null|undefined} res
 * @returns {boolean}
 */
export function isHtmlUploadSuccess(res) {
  return Boolean(res?.success && res?.fileUrl);
}

/**
 * Extract a Blob from a downloadHtml response (plain blob or content-disposition wrapper).
 * @param {Blob|{ data: Blob }|null|undefined} res
 * @returns {Blob|null}
 */
export function extractHtmlDownloadBlob(res) {
  if (res instanceof Blob) {
    return res;
  }
  if (res?.data instanceof Blob) {
    return res.data;
  }
  return null;
}

/**
 * @param {Blob|{ data: Blob }|null|undefined} res
 * @returns {boolean}
 */
export function isHtmlDownloadBlob(res) {
  return extractHtmlDownloadBlob(res) !== null;
}

function isLikelyJsonErrorBlob(blob) {
  if (!(blob instanceof Blob)) {
    return false;
  }
  if (
    blob.type === 'application/json' ||
    blob.type === 'application/problem+json'
  ) {
    return true;
  }
  return blob.size > 0 && blob.size <= JSON_BLOB_MAX_BYTES;
}

async function parseHtmlDownloadJsonPayload(blob) {
  if (!(blob instanceof Blob)) {
    return null;
  }
  if (
    blob.type !== 'application/json' &&
    !(blob.size > 0 && blob.size <= JSON_BLOB_MAX_BYTES)
  ) {
    return null;
  }
  try {
    return JSON.parse(await blob.text());
  } catch {
    return null;
  }
}

/**
 * Parse a JSON error payload from a blob download response.
 * @param {Blob|null|undefined} blob
 * @returns {Promise<string|null>}
 */
export async function parseHtmlDownloadBlobError(blob) {
  if (!isLikelyJsonErrorBlob(blob)) {
    return null;
  }
  try {
    const payload = await parseHtmlDownloadJsonPayload(blob);
    if (!payload) {
      return null;
    }
    const formatted = formatHtmlUploadApiErrorPayload(payload);
    if (formatted) {
      return formatted;
    }
    if (payload?.success === false) {
      return DEFAULT_DOWNLOAD_ERROR;
    }
  } catch {
    return null;
  }
  return null;
}

function toHtmlContentBlob(blob) {
  if (blob.type === 'text/html' || blob.type === 'application/xhtml+xml') {
    return blob;
  }
  if (HTML_BLOB_TYPES.has(blob.type)) {
    return new Blob([blob], { type: 'text/html' });
  }
  return null;
}

/**
 * Resolve a streamed HTML download response into HTML text or an error.
 * @param {{ data?: Blob, headers?: object }|Blob|null|undefined} res
 * @returns {Promise<
 *   | { ok: true, htmlText: string, assetBaseUrl: string|null }
 *   | { ok: false, error: string }
 * >}
 */
export async function resolveHtmlDownloadResponse(res) {
  const headers = res?.headers || {};
  const blob = extractHtmlDownloadBlob(res?.data ?? res);
  if (!blob) {
    return { ok: false, error: DEFAULT_DOWNLOAD_ERROR };
  }

  const isJsonContentType =
    blob.type === 'application/json' ||
    blob.type === 'application/problem+json';
  const shouldInspectForJson =
    isJsonContentType || (blob.size > 0 && blob.size <= JSON_BLOB_MAX_BYTES);

  let htmlText = null;
  if (shouldInspectForJson) {
    const text = await blob.text();
    if (isJsonContentType || text.trimStart().startsWith('{')) {
      try {
        const payload = JSON.parse(text);
        const formatted = formatHtmlUploadApiErrorPayload(payload);
        return {
          ok: false,
          error: formatted || DEFAULT_DOWNLOAD_ERROR,
        };
      } catch {
        if (isJsonContentType) {
          return { ok: false, error: DEFAULT_DOWNLOAD_ERROR };
        }
      }
    }
    htmlText = text;
  }

  const htmlBlob = toHtmlContentBlob(blob);
  if (!htmlBlob && !htmlText) {
    return { ok: false, error: DEFAULT_DOWNLOAD_ERROR };
  }

  if (!htmlText) {
    htmlText = await (htmlBlob || blob).text();
  }

  return {
    ok: true,
    htmlText,
    assetBaseUrl: resolveHtmlAssetBaseUrl(headers),
  };
}

/**
 * Storage key sent to HTML download/replace APIs from a stored link/fileUrl.
 * @param {string} fileUrl
 * @returns {string}
 */
export function normalizeHtmlFileKey(fileUrl) {
  if (!fileUrl) {
    return '';
  }
  return String(fileUrl).replace(/^https:\/\//, '');
}

/**
 * Derive a display name from a stored fileUrl when fileName is not available.
 * @param {string} fileUrl
 * @returns {string}
 */
export function displayNameFromHtmlFileUrl(fileUrl) {
  if (!fileUrl) {
    return '';
  }
  const dashIndex = fileUrl.indexOf('-');
  if (dashIndex >= 0 && dashIndex < fileUrl.length - 1) {
    return fileUrl.slice(dashIndex + 1);
  }
  return fileUrl;
}
